
import React, { useCallback, useEffect, useMemo, useState } from "react";
import "./VendorRatingsReviews.css";

const API_BASE = (
  process.env.REACT_APP_API_URL || "https://bmtvendorvendorfinal.onrender.com"
).replace(/\/+$/, "");

const VERTICALS = [
  { value: "all", label: "All Verticals" },
  { value: "cab", label: "Cab" },
  { value: "bus", label: "Bus" },
  { value: "stay", label: "Stay" },
  { value: "hotel", label: "Hotel" },
  { value: "homestay", label: "Homestay" },
  { value: "resort", label: "Resort" },
  { value: "villa", label: "Villa" },
  { value: "apartment", label: "Apartment" },
  { value: "activity", label: "Activities" },
  { value: "tour", label: "Tours" },
  { value: "cruise", label: "Cruise" },
  { value: "nightclub", label: "Nightclub" },
  { value: "event", label: "Events" },
  { value: "holiday-package", label: "Holiday Packages" },
];

const getToken = () =>
  localStorage.getItem("vendorToken") ||
  localStorage.getItem("token") ||
  localStorage.getItem("accessToken") ||
  "";

const getErrorMessage = (data) =>
  data?.message || data?.error || "Something went wrong.";

const formatDate = (value) => {
  if (!value) return "Date unavailable";

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Date unavailable";

  return date.toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const getInitials = (name) =>
  String(name || "Customer")
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() || "")
    .join("");

function StarRating({ rating = 0, large = false }) {
  const rounded = Math.round(Number(rating) || 0);

  return (
    <span
      className={`rr-stars ${large ? "rr-stars-large" : ""}`}
      aria-label={`${rating} out of 5 stars`}
    >
      {[1, 2, 3, 4, 5].map((star) => (
        <span key={star} className={star <= rounded ? "filled" : ""}>
          ★
        </span>
      ))}
    </span>
  );
}

function StatCard({ label, value, detail, icon, className = "" }) {
  return (
    <div className={`rr-stat-card ${className}`}>
      <div className="rr-stat-top">
        <span className="rr-stat-label">{label}</span>
        <span className="rr-stat-icon">{icon}</span>
      </div>
      <div className="rr-stat-value">{value}</div>
      <div className="rr-stat-detail">{detail}</div>
    </div>
  );
}

export default function VendorRatingsReviews() {
  const [vertical, setVertical] = useState("all");
  const [ratingFilter, setRatingFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("ALL");
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);

  const [reviews, setReviews] = useState([]);
  const [summary, setSummary] = useState({
    total: 0,
    averageRating: 0,
    replied: 0,
    unanswered: 0,
    pending: 0,
    countsByRating: { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 },
  });

  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0,
  });

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [replyingId, setReplyingId] = useState("");
  const [replyText, setReplyText] = useState("");
  const [savingReply, setSavingReply] = useState(false);

  const authHeaders = useCallback(() => {
    const token = getToken();

    return {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    };
  }, []);

  const loadReviews = useCallback(
    async (showLoader = true) => {
      if (showLoader) setLoading(true);

      setError("");
      setNotice("");

      try {
        const params = new URLSearchParams({
          vertical,
          status: statusFilter,
          page: String(page),
          limit: "10",
        });

        if (ratingFilter !== "all") {
          params.set("rating", ratingFilter);
        }

        if (search) params.set("search", search);

        const response = await fetch(
          `${API_BASE}/api/vendor/reviews?${params.toString()}`,
          {
            method: "GET",
            headers: authHeaders(),
          }
        );

        const data = await response.json().catch(() => ({}));

        if (!response.ok || data.success === false) {
          throw new Error(
            response.status === 401
              ? "Login session expire ho gaya. Dobara login kar."
              : getErrorMessage(data)
          );
        }

        setReviews(Array.isArray(data.reviews) ? data.reviews : []);
        setSummary((previous) => ({
          ...previous,
          ...(data.summary || {}),
          countsByRating: {
            ...previous.countsByRating,
            ...(data.summary?.countsByRating || {}),
          },
        }));

        setPagination(
          data.pagination || {
            page,
            limit: 10,
            total: data.reviews?.length || 0,
            totalPages: 1,
          }
        );
      } catch (err) {
        setError(err.message || "Reviews load nahi ho paaye.");
        setReviews([]);
      } finally {
        setLoading(false);
      }
    },
    [
      vertical,
      statusFilter,
      ratingFilter,
      search,
      page,
      authHeaders,
    ]
  );

  useEffect(() => {
    loadReviews();
  }, [loadReviews]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setPage(1);
      setSearch(searchInput.trim());
    }, 350);

    return () => clearTimeout(timer);
  }, [searchInput]);

  const ratingDistribution = useMemo(() => {
    const counts = summary.countsByRating || {};
    const total = Number(summary.total) || 0;

    return [5, 4, 3, 2, 1].map((star) => ({
      star,
      count: Number(counts[star]) || 0,
      percent: total
        ? Math.min(100, ((Number(counts[star]) || 0) / total) * 100)
        : 0,
    }));
  }, [summary]);

  const handleVerticalChange = (value) => {
    setVertical(value);
    setPage(1);
  };

  const handleRatingChange = (value) => {
    setRatingFilter(value);
    setPage(1);
  };

  const handleStatusChange = (value) => {
    setStatusFilter(value);
    setPage(1);
  };

  const startReply = (review) => {
    setReplyingId(review._id);
    setReplyText(review.vendorReply?.message || review.replyText || "");
    setNotice("");
    setError("");
  };

  const saveReply = async (review) => {
    const message = replyText.trim();

    if (!message) {
      setError("Reply likh kar save kar, bhai.");
      return;
    }

    if (message.length > 2000) {
      setError("Reply maximum 2000 characters ka ho sakta hai.");
      return;
    }

    setSavingReply(true);
    setError("");
    setNotice("");

    try {
      const response = await fetch(
        `${API_BASE}/api/vendor/reviews/${review._id}/reply`,
        {
          method: "PATCH",
          headers: authHeaders(),
          body: JSON.stringify({ message }),
        }
      );

      const data = await response.json().catch(() => ({}));

      if (!response.ok || data.success === false) {
        throw new Error(getErrorMessage(data));
      }

      setNotice("Reply successfully save ho gaya.");
      setReplyingId("");
      setReplyText("");
      await loadReviews(false);
    } catch (err) {
      setError(err.message || "Reply save nahi ho paaya.");
    } finally {
      setSavingReply(false);
    }
  };

  const clearFilters = () => {
    setVertical("all");
    setRatingFilter("all");
    setStatusFilter("ALL");
    setSearchInput("");
    setSearch("");
    setPage(1);
  };

  return (
    <main className="rr-page">
      <header className="rr-page-header">
        <div>
          <div className="rr-eyebrow">VENDOR MANAGEMENT</div>
          <h1>Ratings &amp; Reviews</h1>
          <p>
            Customer feedback dekho, apni service improve karo aur reviews ka
            reply do.
          </p>
        </div>

        <button
          type="button"
          className="rr-refresh-btn"
          onClick={() => loadReviews()}
          disabled={loading}
        >
          <span className={loading ? "rr-refresh-spinning" : ""}>↻</span>
          Refresh
        </button>
      </header>

      {error && (
        <div className="rr-alert rr-alert-error" role="alert">
          <span>!</span>
          <div>
            <strong>Action required</strong>
            <p>{error}</p>
          </div>
          <button type="button" onClick={() => setError("")} aria-label="Close">
            ×
          </button>
        </div>
      )}

      {notice && (
        <div className="rr-alert rr-alert-success" role="status">
          <span>✓</span>
          <p>{notice}</p>
          <button
            type="button"
            onClick={() => setNotice("")}
            aria-label="Close"
          >
            ×
          </button>
        </div>
      )}

      <section className="rr-stats-grid">
        <StatCard
          label="Total Reviews"
          value={summary.total ?? 0}
          detail="Reviews in selected vertical"
          icon="▤"
        />
        <StatCard
          label="Average Rating"
          value={Number(summary.averageRating || 0).toFixed(1)}
          detail={
            <StarRating rating={summary.averageRating || 0} />
          }
          icon="★"
          className="rr-stat-rating"
        />
        <StatCard
          label="Replied Reviews"
          value={summary.replied ?? 0}
          detail="Replies saved"
          icon="↩"
        />
        <StatCard
          label="Awaiting Reply"
          value={summary.unanswered ?? 0}
          detail="Reviews without a reply"
          icon="◷"
        />
      </section>

      <section className="rr-main-grid">
        <div className="rr-panel rr-rating-panel">
          <div className="rr-panel-heading">
            <div>
              <h2>Rating Overview</h2>
              <p>Customer satisfaction at a glance</p>
            </div>
            <span className="rr-overview-icon">★</span>
          </div>

          <div className="rr-rating-total">
            <strong>{Number(summary.averageRating || 0).toFixed(1)}</strong>
            <div>
              <StarRating rating={summary.averageRating || 0} large />
              <span>{summary.total || 0} total reviews</span>
            </div>
          </div>

          <div className="rr-distribution">
            {ratingDistribution.map((item) => (
              <button
                type="button"
                className={`rr-rating-row ${
                  ratingFilter === String(item.star) ? "selected" : ""
                }`}
                key={item.star}
                onClick={() =>
                  handleRatingChange(
                    ratingFilter === String(item.star)
                      ? "all"
                      : String(item.star)
                  )
                }
                title={`Filter ${item.star}-star reviews`}
              >
                <span>{item.star} ★</span>
                <span className="rr-progress-track">
                  <span
                    className="rr-progress-fill"
                    style={{ width: `${item.percent}%` }}
                  />
                </span>
                <span className="rr-rating-count">{item.count}</span>
              </button>
            ))}
          </div>
        </div>

        <div className="rr-panel rr-tips-panel">
          <div className="rr-panel-heading">
            <div>
              <h2>Review Insights</h2>
              <p>Manage your customer feedback</p>
            </div>
            <span className="rr-overview-icon">✦</span>
          </div>

          <div className="rr-insight-item">
            <span className="rr-insight-check">✓</span>
            <div>
              <strong>Reply to your customers</strong>
              <p>Professional replies build customer trust.</p>
            </div>
          </div>

          <div className="rr-insight-item">
            <span className="rr-insight-check">✓</span>
            <div>
              <strong>Review low ratings</strong>
              <p>Understand feedback and improve your service.</p>
            </div>
          </div>

          <div className="rr-insight-item">
            <span className="rr-insight-check">✓</span>
            <div>
              <strong>Check every vertical</strong>
              <p>Use the filters to review each business category.</p>
            </div>
          </div>
        </div>
      </section>

      <section className="rr-panel rr-reviews-panel">
        <div className="rr-reviews-heading">
          <div>
            <h2>Customer Reviews</h2>
            <p>Read feedback and manage your replies</p>
          </div>
          <span className="rr-result-count">
            {pagination.total ?? 0} results
          </span>
        </div>

        <div className="rr-filter-bar">
          <label className="rr-search">
            <span>⌕</span>
            <input
              type="search"
              placeholder="Search customer, listing or review..."
              value={searchInput}
              onChange={(event) => setSearchInput(event.target.value)}
            />
          </label>

          <select
            aria-label="Filter by vertical"
            value={vertical}
            onChange={(event) => handleVerticalChange(event.target.value)}
          >
            {VERTICALS.map((item) => (
              <option key={item.value} value={item.value}>
                {item.label}
              </option>
            ))}
          </select>

          <select
            aria-label="Filter by rating"
            value={ratingFilter}
            onChange={(event) => handleRatingChange(event.target.value)}
          >
            <option value="all">All Ratings</option>
            {[5, 4, 3, 2, 1].map((star) => (
              <option key={star} value={star}>
                {star} Stars
              </option>
            ))}
          </select>

          <select
            aria-label="Filter by status"
            value={statusFilter}
            onChange={(event) => handleStatusChange(event.target.value)}
          >
            <option value="ALL">All Statuses</option>
            <option value="APPROVED">Approved</option>
            <option value="PENDING">Pending</option>
            <option value="REJECTED">Rejected</option>
            <option value="HIDDEN">Hidden</option>
          </select>

          <button
            type="button"
            className="rr-clear-btn"
            onClick={clearFilters}
          >
            Clear
          </button>
        </div>

        {loading ? (
          <div className="rr-state">
            <span className="rr-spinner" />
            <strong>Loading reviews...</strong>
            <p>Fetching your review data from the backend.</p>
          </div>
        ) : reviews.length === 0 ? (
          <div className="rr-state">
            <div className="rr-empty-icon">☆</div>
            <strong>No reviews found</strong>
            <p>
              Is filter ke liye reviews nahi mile. Filters clear karke dobara
              check kar.
            </p>
            <button
              type="button"
              className="rr-primary-btn"
              onClick={clearFilters}
            >
              Clear filters
            </button>
          </div>
        ) : (
          <div className="rr-review-list">
            {reviews.map((review) => {
              const reply =
                review.vendorReply?.message || review.replyText || "";
              const isReplying = replyingId === review._id;

              return (
                <article className="rr-review-card" key={review._id}>
                  <div className="rr-review-avatar">
                    {getInitials(review.customerName)}
                  </div>

                  <div className="rr-review-content">
                    <div className="rr-review-top">
                      <div>
                        <h3>{review.customerName || "Customer"}</h3>
                        <span className="rr-review-date">
                          {formatDate(review.createdAt)}
                        </span>
                      </div>

                      <div className="rr-review-tags">
                        <span className="rr-vertical-tag">
                          {review.verticalLabel ||
                            VERTICALS.find(
                              (item) => item.value === review.vertical
                            )?.label ||
                            review.vertical ||
                            "Review"}
                        </span>
                        <span
                          className={`rr-status-tag rr-status-${String(
                            review.status || "PENDING"
                          ).toLowerCase()}`}
                        >
                          {review.status || "PENDING"}
                        </span>
                      </div>
                    </div>

                    <div className="rr-review-rating">
                      <StarRating rating={review.rating} />
                      <span>{Number(review.rating || 0)}/5</span>
                    </div>

                    <div className="rr-listing-name">
                      {review.listingName || "Listing"}
                    </div>

                    {review.title && (
                      <h4 className="rr-review-title">{review.title}</h4>
                    )}

                    <p className="rr-review-comment">
                      {review.comment || "No written comment provided."}
                    </p>

                    {reply && !isReplying && (
                      <div className="rr-existing-reply">
                        <div className="rr-reply-heading">
                          <strong>Your reply</strong>
                          <span>Vendor</span>
                        </div>
                        <p>{reply}</p>
                      </div>
                    )}

                    {isReplying && (
                      <div className="rr-reply-editor">
                        <label htmlFor={`reply-${review._id}`}>
                          Your response
                        </label>
                        <textarea
                          id={`reply-${review._id}`}
                          value={replyText}
                          onChange={(event) =>
                            setReplyText(event.target.value)
                          }
                          maxLength={2000}
                          rows={4}
                          placeholder="Thank the customer or respond professionally..."
                        />
                        <div className="rr-editor-footer">
                          <span>{replyText.length}/2000</span>
                          <div>
                            <button
                              type="button"
                              className="rr-secondary-btn"
                              onClick={() => {
                                setReplyingId("");
                                setReplyText("");
                              }}
                              disabled={savingReply}
                            >
                              Cancel
                            </button>
                            <button
                              type="button"
                              className="rr-primary-btn"
                              onClick={() => saveReply(review)}
                              disabled={savingReply || !replyText.trim()}
                            >
                              {savingReply ? "Saving..." : "Save reply"}
                            </button>
                          </div>
                        </div>
                      </div>
                    )}

                    {!isReplying && (
                      <div className="rr-review-actions">
                        <button
                          type="button"
                          className="rr-reply-btn"
                          onClick={() => startReply(review)}
                        >
                          <span>↩</span>
                          {reply ? "Edit reply" : "Reply to review"}
                        </button>
                      </div>
                    )}
                  </div>
                </article>
              );
            })}
          </div>
        )}

        {!loading && pagination.totalPages > 1 && (
          <div className="rr-pagination">
            <span>
              Page {pagination.page || page} of {pagination.totalPages}
            </span>
            <div>
              <button
                type="button"
                className="rr-secondary-btn"
                disabled={page <= 1}
                onClick={() => setPage((current) => Math.max(1, current - 1))}
              >
                Previous
              </button>
              <button
                type="button"
                className="rr-secondary-btn"
                disabled={page >= pagination.totalPages}
                onClick={() =>
                  setPage((current) =>
                    Math.min(pagination.totalPages, current + 1)
                  )
                }
              >
                Next
              </button>
            </div>
          </div>
        )}
      </section>
    </main>
  );
}
